import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaInfoCircle } from 'react-icons/fa';
import { FiInfo } from 'react-icons/fi';
import LoadingOverlay from './LoadingOverlay';
import TextModal from './TextModal';
import { useToast } from './ToastContext';
import CustomSelect from './CustomSelect';
import './../Styles/Employee.css';

const apiUrl = process.env.REACT_APP_API_URL;

const RATING_OPTIONS = [
    { value: '1', label: '1' },
    { value: '2', label: '2' },
    { value: '3', label: '3' },
    { value: '4', label: '4' },
    { value: '5', label: '5' }
];

const COMMUNICATED_OPTIONS = [
    { value: 'yes', label: 'Yes' },
    { value: 'no', label: 'No' }
];

const GoalAssessmentTable = ({ 
    employeeId: propEmployeeId,
    onGoalSubmitted 
}) => {
    const showToast = useToast();
    const [assignedGoals, setAssignedGoals] = useState([]);
    const [quarters, setQuarters] = useState([]);
    const [selectedQuarter, setSelectedQuarter] = useState('');
    const [goalDescriptions, setGoalDescriptions] = useState({});
    const [attachmentLinks, setAttachmentLinks] = useState({});
    const [selfRatings, setSelfRatings] = useState({});
    const [communicatedStatuses, setCommunicatedStatuses] = useState({});
    const [savedGoals, setSavedGoals] = useState({});
    const [errors, setErrors] = useState({});
    const [loadingGoalId, setLoadingGoalId] = useState(null);
    const [isInstructionsOpen, setInstructionsOpen] = useState(false);
    const [infoGoal, setInfoGoal] = useState(null);

    // TextModal state
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [currentField, setCurrentField] = useState(null);
    const [currentGoalId, setCurrentGoalId] = useState(null);
    const [initialText, setInitialText] = useState('');

    const activeEmployeeId = propEmployeeId || localStorage.getItem('employeeId');

    // Fetch Quarters
    useEffect(() => {
        const defaultQuarters = [
            { id: 'q1', quarter: 'Q1 2024' },
            { id: 'q2', quarter: 'Q2 2024' },
            { id: 'q3', quarter: 'Q3 2024' },
            { id: 'q4', quarter: 'Q4 2024' }
        ];

        axios.get(`${apiUrl}/api/quarters`)
            .then(res => {
                const data = res.data || [];
                if (data.length > 0) {
                    const qs = data.map(q => typeof q === 'string' ? q : (q.quarter || q.quarter_name || q.name || ''));
                    setQuarters(qs);
                } else {
                    setQuarters(defaultQuarters.map(q => q.quarter));
                }
            })
            .catch(err => {
                console.error('Error fetching quarters:', err);
                setQuarters(defaultQuarters.map(q => q.quarter));
            });
    }, []);

    // Fetch Assigned Goals
    useEffect(() => {
        if (!activeEmployeeId) return;

        axios.get(`${apiUrl}/api/employee-goals/${activeEmployeeId}`)
            .then(res => {
                const uniqueGoals = Array.from(new Map(res.data.map(goal => [goal._id || goal.id, goal])).values());
                setAssignedGoals(uniqueGoals);
            })
            .catch(err => console.error('Error fetching employee goals:', err));
    }, [activeEmployeeId]);

    // Modal controls
    const openModal = (field, goalId, existingText) => {
        setCurrentField(field);
        setCurrentGoalId(goalId);
        if (field === 'description') {
            setInitialText(goalDescriptions[goalId] || existingText || '');
        } else if (field === 'attachment') {
            setInitialText(attachmentLinks[goalId] || existingText || '');
        }
        setModalIsOpen(true);
    };

    const closeModal = () => {
        setModalIsOpen(false);
        setInitialText('');
    };

    const handleTextSubmit = (submittedText) => {
        if (currentField === 'description' && currentGoalId !== null) {
            setGoalDescriptions(prev => ({ ...prev, [currentGoalId]: submittedText }));
            if (submittedText.trim()) {
                setErrors(prev => {
                    const next = { ...prev };
                    delete next[`${currentGoalId}_description`];
                    return next;
                });
            }
        } else if (currentField === 'attachment' && currentGoalId !== null) {
            setAttachmentLinks(prev => ({ ...prev, [currentGoalId]: submittedText }));
        }
        closeModal();
    };

    // Save row (client-side lock)
    const isSaveEnabled = (goalId) => {
        return (
            (goalDescriptions[goalId] || '').trim() !== '' ||
            (selfRatings[goalId] || '') !== '' ||
            (communicatedStatuses[goalId] || '') !== ''
        );
    };

    const handleSaveRow = (goalId) => {
        if (!selectedQuarter) {
            showToast('Please select a quarter before saving', 'warning');
            return;
        }
        setSavedGoals(prev => ({ ...prev, [goalId]: true }));
        showToast('Goal saved successfully.', 'success');
    };

    const handleEditRow = (goalId) => {
        setSavedGoals(prev => ({ ...prev, [goalId]: false }));
    };

    // Submit single goal row
    const handleSubmitRow = async (goalId) => {
        if (!selectedQuarter) {
            showToast('Please select a quarter before submitting', 'warning');
            return;
        }

        const description = (goalDescriptions[goalId] || '').trim();
        const rating = selfRatings[goalId] || '';
        const communicated = communicatedStatuses[goalId] || '';

        let newErrors = { ...errors };
        let hasError = false;

        if (!description) {
            newErrors[`${goalId}_description`] = 'Description text is required';
            showToast('Description text is mandatory for submission.', 'warning');
            hasError = true;
        } else {
            delete newErrors[`${goalId}_description`];
        }

        if (!rating) {
            newErrors[`${goalId}_rating`] = 'Self rating is required';
            showToast('Self Rating is mandatory for submission.', 'warning');
            hasError = true;
        } else {
            delete newErrors[`${goalId}_rating`];
        }

        if (!communicated) {
            newErrors[`${goalId}_communicated`] = 'Communicated status is required';
            showToast('Communicated status is mandatory for submission.', 'warning');
            hasError = true;
        } else {
            delete newErrors[`${goalId}_communicated`];
        }

        if (hasError) {
            setErrors(newErrors);
            return;
        }

        setErrors(prev => {
            const next = { ...prev };
            delete next[`${goalId}_description`];
            delete next[`${goalId}_rating`];
            delete next[`${goalId}_communicated`];
            return next;
        });

        setLoadingGoalId(goalId);
        try {
            await axios.post(`${apiUrl}/api/update-goal-row`, {
                goalId,
                self_assessment: goalDescriptions[goalId] || '',
                attachments: attachmentLinks[goalId] || '',
                self_rating: selfRatings[goalId] || '',
                communicated: communicatedStatuses[goalId] || '',
                employeeId: activeEmployeeId,
                quarter: selectedQuarter,
                is_submitted: true
            });

            // Remove submitted goal from view
            setAssignedGoals(prev => prev.filter(g => (g._id || g.id) !== goalId));
            showToast('Goal submitted successfully.', 'success');
            if (onGoalSubmitted) {
                onGoalSubmitted(goalId);
            }
        } catch (error) {
            if (error.response && error.response.status === 400) {
                showToast('Goal has already been submitted for this quarter.', 'error');
            } else {
                console.error('Error updating goal:', error);
                showToast('Error updating goal.', 'error');
            }
        } finally {
            setLoadingGoalId(null);
        }
    };

    return (
        <div className="employee-body">
            {loadingGoalId && <LoadingOverlay />}
            <div className="goals-header-container">
                <h2>Assigned Goals</h2>
                <div className="quarter-history-container">
                    <div className="quarter-selector">
                        <label htmlFor="quarter">
                            Select Quarter <span className="required-star" style={{ color: 'red' }}>*</span>:
                        </label>
                        <CustomSelect
                            value={selectedQuarter}
                            onChange={(val) => setSelectedQuarter(val)}
                            options={quarters.map(q => ({ value: q, label: q }))}
                            placeholder="Select Quarter"
                            style={{ minWidth: '180px' }}
                        />
                    </div>
                    <button 
                        className="instructions-btn" 
                        onClick={() => setInstructionsOpen(true)} 
                        title="Instructions"
                    >
                        <FaInfoCircle size={24} />
                    </button>
                </div>
            </div>

            <div className="table-container pms-table-wrap">
                <table className="goals-table pms-table">
                    <thead>
                        <tr>
                            <th>GOAL DESCRIPTION</th>
                            <th>DESCRIPTION TEXT <span className="required-star" style={{ color: 'red' }}>*</span></th>
                            <th>ATTACHMENT</th>
                            <th>SELF RATING <span className="required-star" style={{ color: 'red' }}>*</span></th>
                            <th>COMMUNICATED <span className="required-star" style={{ color: 'red' }}>*</span></th>
                            <th>SAVE</th>
                            <th>SUBMIT</th>
                        </tr>
                    </thead>
                    <tbody>
                        {assignedGoals.length > 0 ? (
                            assignedGoals.map((goal) => {
                                const gId = goal._id || goal.id;
                                return (
                                    <tr key={gId}>
                                        <td style={{ fontWeight: '500' }}>
                                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                                                <span>{goal.goal_name || goal.goal_description || 'N/A'}</span>
                                                <button
                                                    type="button"
                                                    className="goal-info-btn"
                                                    onClick={() => setInfoGoal(goal)}
                                                    title="View Goal Details"
                                                    aria-label="View Goal Details"
                                                >
                                                    <FiInfo size={16} />
                                                </button>
                                            </div>
                                        </td>
                                        <td>
                                            {savedGoals[gId] ? (
                                                <span>{goalDescriptions[gId]}</span>
                                            ) : (
                                                <>
                                                    <input
                                                        type="text"
                                                        value={goalDescriptions[gId] || ''}
                                                        onChange={(e) => {
                                                            const val = e.target.value;
                                                            setGoalDescriptions(prev => ({ ...prev, [gId]: val }));
                                                            if (val.trim()) {
                                                                setErrors(prev => {
                                                                    const next = { ...prev };
                                                                    delete next[`${gId}_description`];
                                                                    return next;
                                                                });
                                                            }
                                                        }}
                                                        onClick={() => openModal('description', gId, goalDescriptions[gId] || '')}
                                                        placeholder="Enter description"
                                                    />
                                                    {errors[`${gId}_description`] && (
                                                        <span className="error-message">{errors[`${gId}_description`]}</span>
                                                    )}
                                                </>
                                            )}
                                        </td>
                                        <td>
                                            {savedGoals[gId] ? (
                                                attachmentLinks[gId] ? (
                                                    <a href={attachmentLinks[gId]} target="_blank" rel="noopener noreferrer">
                                                        View Attachment
                                                    </a>
                                                ) : (
                                                    <span>—</span>
                                                )
                                            ) : (
                                                <input
                                                    type="text"
                                                    value={attachmentLinks[gId] || ''}
                                                    onChange={(e) => setAttachmentLinks(prev => ({ ...prev, [gId]: e.target.value }))}
                                                    onClick={() => openModal('attachment', gId, attachmentLinks[gId] || '')}
                                                    placeholder="Enter attachment link"
                                                />
                                            )}
                                        </td>
                                        <td>
                                            {savedGoals[gId] ? (
                                                <span>{selfRatings[gId]}</span>
                                            ) : (
                                                <>
                                                    <CustomSelect
                                                        value={selfRatings[gId] || ''}
                                                        onChange={(val) => {
                                                            setSelfRatings(prev => ({ ...prev, [gId]: val }));
                                                            if (val) {
                                                                setErrors(prev => {
                                                                    const next = { ...prev };
                                                                    delete next[`${gId}_rating`];
                                                                    return next;
                                                                });
                                                            }
                                                        }}
                                                        options={RATING_OPTIONS}
                                                        placeholder="Select"
                                                        error={!!errors[`${gId}_rating`]}
                                                    />
                                                    {errors[`${gId}_rating`] && (
                                                        <span className="error-message">{errors[`${gId}_rating`]}</span>
                                                    )}
                                                </>
                                            )}
                                        </td>
                                        <td>
                                            {savedGoals[gId] ? (
                                                <span>{communicatedStatuses[gId] === 'yes' ? 'Yes' : communicatedStatuses[gId] === 'no' ? 'No' : (communicatedStatuses[gId] || '—')}</span>
                                            ) : (
                                                <>
                                                    <CustomSelect
                                                        value={communicatedStatuses[gId] || ''}
                                                        onChange={(val) => {
                                                            setCommunicatedStatuses(prev => ({ ...prev, [gId]: val }));
                                                            if (val) {
                                                                setErrors(prev => {
                                                                    const next = { ...prev };
                                                                    delete next[`${gId}_communicated`];
                                                                    return next;
                                                                });
                                                            }
                                                        }}
                                                        options={COMMUNICATED_OPTIONS}
                                                        placeholder="Select"
                                                        error={!!errors[`${gId}_communicated`]}
                                                    />
                                                    {errors[`${gId}_communicated`] && (
                                                        <span className="error-message">{errors[`${gId}_communicated`]}</span>
                                                    )}
                                                </>
                                            )}
                                        </td>
                                        <td>
                                            <button
                                                onClick={() => savedGoals[gId] ? handleEditRow(gId) : handleSaveRow(gId)}
                                                disabled={!savedGoals[gId] && !isSaveEnabled(gId)}
                                            >
                                                {savedGoals[gId] ? 'Edit' : 'Save'}
                                            </button>
                                        </td>
                                        <td>
                                            <button
                                                onClick={() => handleSubmitRow(gId)}
                                                disabled={loadingGoalId === gId}
                                                className="submit-btn"
                                            >
                                                {loadingGoalId === gId ? 'Submitting...' : 'Submit'}
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan="7" style={{ textAlign: 'center', padding: '24px', color: 'var(--text-secondary)' }}>
                                    No assigned goals found for your account.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* TextModal for editing long text */}
            <TextModal
                isOpen={modalIsOpen}
                onRequestClose={closeModal}
                onTextSubmit={handleTextSubmit}
                initialText={initialText}
                field={currentField}
            />

            {/* Instructions Modal */}
            <div className={`instructions-modal ${isInstructionsOpen ? 'open' : ''}`}>
                <h3>Goal Submission and Review Process</h3>
                <ol>
                    <li>Goals will be dynamically fetched in your dashboard once your manager assigns goals to you.</li>
                    <li>You need to select the particular quarter from the dropdown for which you are submitting the goal.</li>
                    <li>When entering your goal description, click the text input to use the full text editor or paste your notes.</li>
                    <li>To upload certifications or attachments, store the files in OneDrive and paste the shared link into the "Attachment" field.</li>
                    <li>After submitting a goal, your manager/lead will be notified automatically.</li>
                    <li>You can view all submitted goals in the "Submitted" section.</li>
                    <li>If you encounter any issues, please reach out to <a href="mailto:pms@tsqsinc.com">pms@tsqsinc.com</a> for assistance.</li>
                </ol>
                <button className="close-instructions" onClick={() => setInstructionsOpen(false)}>Close</button>
            </div>

            {/* Goal Information Pop-up Modal */}
            {infoGoal && (
                <div className="pms-overlay modal-overlay" onClick={() => setInfoGoal(null)}>
                    <div 
                        className="pms-modal modal-content goal-info-modal" 
                        onClick={(e) => e.stopPropagation()}
                        style={{ 
                            maxWidth: '520px', 
                            width: '92%', 
                            padding: '24px 28px', 
                            borderRadius: '10px',
                            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.2), 0 10px 10px -5px rgba(0, 0, 0, 0.1)'
                        }}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px', borderBottom: '1px solid var(--border, #e2e8f0)', paddingBottom: '12px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <div style={{ width: '32px', height: '32px', borderRadius: '50%', backgroundColor: 'rgba(234, 88, 12, 0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ea580c' }}>
                                    <FiInfo size={18} />
                                </div>
                                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: 'var(--text-primary, #0f172a)' }}>
                                    Goal Information
                                </h3>
                            </div>
                            <button
                                type="button"
                                onClick={() => setInfoGoal(null)}
                                style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: 'var(--text-secondary, #64748b)', padding: '4px', lineHeight: 1 }}
                                aria-label="Close"
                            >
                                ✕
                            </button>
                        </div>

                        <div style={{ marginBottom: '16px' }}>
                            <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-secondary, #64748b)', marginBottom: '6px' }}>
                                Goal Name
                            </label>
                            <div style={{ fontSize: '15px', fontWeight: '600', color: 'var(--text-primary, #1e293b)', padding: '10px 14px', backgroundColor: 'var(--bg-secondary, #f8fafc)', borderRadius: '6px', border: '1px solid var(--border, #e2e8f0)' }}>
                                {infoGoal.goal_name || infoGoal.goal_description || 'Goal'}
                            </div>
                        </div>

                        <div style={{ marginBottom: '22px' }}>
                            <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-secondary, #64748b)', marginBottom: '6px' }}>
                                Goal Description
                            </label>
                            <div style={{ 
                                fontSize: '14px', 
                                lineHeight: '1.6', 
                                color: 'var(--text-primary, #334155)', 
                                padding: '12px 14px', 
                                backgroundColor: 'var(--bg-secondary, #f8fafc)', 
                                borderRadius: '6px', 
                                border: '1px solid var(--border, #e2e8f0)',
                                maxHeight: '220px',
                                overflowY: 'auto',
                                whiteSpace: 'pre-wrap'
                            }}>
                                {infoGoal.description ? (
                                    infoGoal.description
                                ) : (
                                    <span style={{ color: 'var(--text-muted, #94a3b8)', fontStyle: 'italic' }}>
                                        No detailed description provided for this goal.
                                    </span>
                                )}
                            </div>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <button
                                type="button"
                                className="pms-btn"
                                onClick={() => setInfoGoal(null)}
                                style={{ 
                                    padding: '8px 24px', 
                                    backgroundColor: '#e67320', 
                                    borderColor: '#e67320', 
                                    color: '#ffffff', 
                                    borderRadius: '6px', 
                                    fontWeight: '600', 
                                    cursor: 'pointer' 
                                }}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default GoalAssessmentTable;
